import * as React from "react";
import { useState, useEffect } from "react";
import ProjectService from "../project/ProjectService";
import { fetchMetaData } from "src/app/polar-SAAS-Components/api-helper";
import { environment } from "src/environments/environment";
import { USER_ROLES } from "../enums";
import MultiSelect from "../polar-SAAS-Components/components/MultiSelectDropDown";
import { useIsMobile } from "src/app/polar-SAAS-Components/utilsNew";


interface UserData {
  lastName: string;
  email: string;
  mobile: string;
  whatsApp: string;
  userRoleId: string;
  references: any[];
}

interface Project {
  id: string;
  name: string;
}

interface AddUserPopupProps {
  onClose: () => void;
  onCreateUser: (userData: UserData) => void;
  error: string;
}

type CountryCode = { code: string; name: string };

const AddUserPopup: React.FC<AddUserPopupProps> = ({ onClose, onCreateUser, error }) => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [selectedRefereces, setSelectedRefereces] = useState<any[]>([]);
  const [formData, setFormData] = useState<UserData>({
    lastName: "",
    email: "",
    mobile: "",
    whatsApp: "",
    userRoleId: "",
    references: [],
  });
  const [errors, setErrors] = useState({
    lastName: "",
    email: "",
    mobile: "",
    whatsApp: "",
    userRoleId: "",
    references: "",
  });
  const [loading, setLoading] = useState(false);

  const userRoles = USER_ROLES.filter((role) => [1000, 2000, 3000].includes(Number(role.userRoleId)));

  const [countryCodes, setCountryCodes] = useState<CountryCode[]>([]);
  const [codesLoading, setCodesLoading] = useState(true);
  const [selectedCountryCode, setSelectedCountryCode] = useState("+94");

  useEffect(() => {
    fetchProjects();
  }, []);

   const isMobile =useIsMobile(768);
 

  const fetchProjects = async () => {
    const projectsAll = await ProjectService.getProjectsForUser();
    setProjects(projectsAll.data.content);
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const cacheKey = "countryCodesCache_v1";
        const cached = localStorage.getItem(cacheKey);
        if (cached) {
          const { at, data } = JSON.parse(cached) as { at: number; data: CountryCode[] };
          if (Date.now() - at < 24 * 60 * 60 * 1000) {
            if (!cancelled) {
              setCountryCodes(data);
              setCodesLoading(false);
            }
            return;
          }
        }
        const res = await fetch("https://restcountries.com/v3.1/all?fields=name,idd");
        if (!res.ok) throw new Error("Failed to fetch country codes");
        type RC = { name?: { common?: string }; idd?: { root?: string; suffixes?: string[] } };
        const raw: RC[] = await res.json();
        const map = new Map<string, string>();
        for (const c of raw) {
          const root = c.idd.root;
          const suffixes = c.idd.suffixes;
          const countryName = c.name.common;
          if (!root || !countryName) continue;
          if (Array.isArray(suffixes) && suffixes.length > 0) {
            for (const s of suffixes) {
              const code = `${root}${s}`;
              if (!map.has(code)) map.set(code, countryName);
            }
          } else {
            const code = root;
            if (!map.has(code)) map.set(code, countryName);
          }
        }
        let arr: CountryCode[] = Array.from(map.entries()).map(([code, name]) => ({
          code,
          name: `${name} (${code})`,
        }));
        const PRIORITY = new Set(["+94", "+91", "+1", "+44"]);
        arr.sort((a, b) => {
          const ap = PRIORITY.has(a.code) ? 0 : 1;
          const bp = PRIORITY.has(b.code) ? 0 : 1;
          if (ap !== bp) return ap - bp;
          return a.name.localeCompare(b.name);
        });
        if (!cancelled) {
          setCountryCodes(arr);
          localStorage.setItem(cacheKey, JSON.stringify({ at: Date.now(), data: arr }));
        }
      } catch {
        if (!codesLoading) return;
        setCountryCodes([
          { code: "+94", name: "Sri Lanka (+94)" },
          { code: "+91", name: "India (+91)" },
          { code: "+1", name: "USA (+1)" },
          { code: "+44", name: "UK (+44)" },
        ]);
      } finally {
        if (!cancelled) setCodesLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    if ((name === "mobile" || name === "whatsApp") && value && !/^\d*$/.test(value)) return;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name as keyof typeof errors]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleCountryCodeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedCountryCode(e.target.value);
  };

  const handleSelecteReferences = (references: any[]) => {
    setSelectedRefereces(references);
    setErrors((prev) => ({ ...prev, references: "" }));
  };

  const validateForm = () => {
    let valid = true;
    const newErrors = {
      lastName: "",
      email: "",
      mobile: "",
      whatsApp: "",
      userRoleId: "",
      references: "",
    };
    if (!formData.lastName.trim()) {
      newErrors.lastName = "Name is required";
      valid = false;
    } else if (formData.lastName.trim().length < 2) {
      newErrors.lastName = "Name must be at least 2 characters";
      valid = false;
    } else if (formData.lastName.trim().length > 50) {
      newErrors.lastName = "Name cannot exceed 50 characters";
      valid = false;
    } else if (!/^[a-zA-Z\s]+$/.test(formData.lastName.trim())) {
      newErrors.lastName = "Name can only contain letters and spaces";
      valid = false;
    }
    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
      valid = false;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Invalid email format";
      valid = false;
    } else if (formData.email.length > 100) {
      newErrors.email = "Email cannot exceed 100 characters";
      valid = false;
    }
    if (!formData.mobile.trim()) {
      newErrors.mobile = "Phone number is required";
      valid = false;
    } else if (!/^\d+$/.test(formData.mobile)) {
      newErrors.mobile = "Phone number must contain only digits";
      valid = false;
    } else if (formData.mobile.length < 9) {
      newErrors.mobile = "Phone number must be at least 9 digits";
      valid = false;
    } else if (formData.mobile.length > 15) {
      newErrors.mobile = "Phone number cannot exceed 15 digits";
      valid = false;
    }
    if (formData.whatsApp && !/^\d+$/.test(formData.whatsApp)) {
      newErrors.whatsApp = "WhatsApp number must contain only digits";
      valid = false;
    } else if (formData.whatsApp && formData.whatsApp.length < 9) {
      newErrors.whatsApp = "WhatsApp number must be at least 9 digits";
      valid = false;
    } else if (formData.whatsApp && formData.whatsApp.length > 15) {
      newErrors.whatsApp = "WhatsApp number cannot exceed 15 digits";
      valid = false;
    }
    if (!formData.userRoleId) {
      newErrors.userRoleId = "Role is required";
      valid = false;
    }
    if (!selectedRefereces || selectedRefereces.length === 0) {
      newErrors.references = "select atleast one project as reference";
      valid = false;
    }
    setErrors(newErrors);
    return valid;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const data = await fetchMetaData();
        const baseUrl = environment.host.substring(0, environment.host.lastIndexOf("/"));
    const hostUrl = (data && data.configuration && data.configuration.configHost) || `${baseUrl}/sign-up`;
    if (validateForm()) {
      const userDataWithFullWhatsapp = {
        ...formData,
        references: selectedRefereces.map((r) => r.value),
        host: hostUrl,
        whatsApp: formData.whatsApp ? `${selectedCountryCode}${formData.whatsApp}` : "",
      };
      setLoading(true);
      await onCreateUser(userDataWithFullWhatsapp);
      setLoading(false);
    }
  };

  const selectBase: React.CSSProperties = {
    width: "120px",
    padding: "10px 8px",
    height: "42px",
    lineHeight: "20px",
    borderRadius: "4px",
    fontSize: "14px",
    fontWeight: 500,
    outline: "none",
    fontFamily: "'Poppins', sans-serif",
    boxSizing: "border-box",
    color: "#324054",
  };

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(50, 64, 84, 0.4)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        zIndex: 1000,
        fontFamily: "'Poppins', sans-serif",
      }}
    >
      <div
        style={{
          backgroundColor: "#FFFFFF",
          padding: "32px",
          width: isMobile ? "100%" : "703px",
          boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)",
        }}
      >
        <div
          style={{
            backgroundColor: "#ffffff",
            boxShadow: "0px 4px 40px 0px rgba(0, 0, 0, 0.1)",
            padding: "20px 24px",
            color: "rgba(60, 66, 87, 1)",
            fontSize: 16,
            fontFamily: "'Poppins', sans-serif",
            height: 69,
            fontWeight: 600,
            margin: "-31px -31px 10px -31px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <h2
            style={{
              fontSize: "20px",
              fontWeight: 600,
              color: "#324054",
              fontFamily: "'Poppins', sans-serif",
              margin: 0,
            }}
          >
            CREATE USERS
          </h2>
          <button
            onClick={onClose}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              fontSize: "38px",
              color: "#324054",
              padding: "0",
              display: "flex",
              alignItems: "center",
            }}
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: "7px" }}>
          {error && error.length > 0 && (
            <div
              style={{
                backgroundColor: "#EF9A9A",
                padding: "8px",
                borderRadius: "4px",
                marginBottom: "12px",
              }}
            >
              <span
                style={{
                  color: "#171111ff",
                  fontSize: "12px",
                  marginTop: "4px",
                  display: "block",
                }}
              >
                {error}
              </span>
            </div>
          )}

          <div>
            <label
              style={{
                display: "block",
                textAlign: "left",
                fontSize: "16px",
                fontWeight: 500,
                color: "#324054",
                marginBottom: "8px",
              }}
            >
              Name <span style={{ color: "#EF4444" }}>*</span>
            </label>
            <input
              style={{
                width: "100%",
                padding: "10px 12px",
                border: `1px solid ${errors.lastName ? "#EF4444" : "#E5E7EB"}`,
                borderRadius: "4px",
                fontSize: "16px",
                fontWeight: 500,
                outline: "none",
                fontFamily: "'Poppins', sans-serif",
                boxSizing: "border-box",
                color: "#324054",
              }}
              type="text"
              name="lastName"
              placeholder="Enter user name here"
              value={formData.lastName}
              onChange={handleChange}
            />
            {errors.lastName && (
              <span
                style={{
                  color: "#EF4444",
                  fontSize: "12px",
                  marginTop: "4px",
                  display: "block",
                }}
              >
                {errors.lastName}
              </span>
            )}
          </div>

          <div>
            <label
              style={{
                display: "block",
                textAlign: "left",
                fontSize: "16px",
                fontWeight: 500,
                color: "#324054",
                marginBottom: "8px",
              }}
            >
              Email <span style={{ color: "#EF4444" }}>*</span>
            </label>
            <input
              style={{
                width: "100%",
                padding: "10px 12px",
                border: `1px solid ${errors.email ? "#EF4444" : "#E5E7EB"}`,
                borderRadius: "4px",
                fontSize: "16px",
                fontWeight: 500,
                outline: "none",
                fontFamily: "'Poppins', sans-serif",
                boxSizing: "border-box",
                color: "#324054",
              }}
              type="email"
              name="email"
              placeholder="example@gmail.com"
              value={formData.email}
              onChange={handleChange}
            />
            {errors.email && (
              <span
                style={{
                  color: "#EF4444",
                  fontSize: "12px",
                  marginTop: "4px",
                  display: "block",
                }}
              >
                {errors.email}
              </span>
            )}
          </div>

          <div>
            <label
              style={{
                display: "block",
                textAlign: "left",
                fontSize: "16px",
                fontWeight: 500,
                color: "#324054",
                marginBottom: "8px",
              }}
            >
              Phone No. <span style={{ color: "#EF4444" }}>*</span>
            </label>
            <input
              style={{
                width: "100%",
                padding: "10px 12px",
                border: `1px solid ${errors.mobile ? "#EF4444" : "#E5E7EB"}`,
                borderRadius: "4px",
                fontSize: "16px",
                fontWeight: 500,
                outline: "none",
                fontFamily: "'Poppins', sans-serif",
                boxSizing: "border-box",
                color: "#324054",
              }}
              type="tel"
              name="mobile"
              placeholder="eg - 07x xx xx xxx"
              value={formData.mobile}
              onChange={handleChange}
              pattern="[0-9]*"
              inputMode="numeric"
            />
            {errors.mobile && (
              <span
                style={{
                  color: "#EF4444",
                  fontSize: "12px",
                  marginTop: "4px",
                  display: "block",
                }}
              >
                {errors.mobile}
              </span>
            )}
          </div>

          <div>
            <label
              style={{
                display: "block",
                textAlign: "left",
                fontSize: "16px",
                fontWeight: 500,
                color: "#324054",
                marginBottom: "8px",
              }}
            >
              WhatsApp No.
            </label>
            <div style={{ display: "flex", gap: "8px" }}>
              <select
                style={{
                  ...selectBase,
                  border: "1px solid #E5E7EB",
                }}
                value={selectedCountryCode}
                onChange={handleCountryCodeChange}
                disabled={codesLoading}
              >
                {codesLoading ? (
                  <option>Loading…</option>
                ) : (
                  countryCodes.map((c) => (
                    <option key={c.code} value={c.code}>
                      {c.code}
                    </option>
                  ))
                )}
              </select>
              <input
                style={{
                  flex: 1,
                  padding: "10px 12px",
                  border: "1px solid #E5E7EB",
                  borderRadius: "4px",
                  fontSize: "16px",
                  fontWeight: 500,
                  outline: "none",
                  fontFamily: "'Poppins', sans-serif",
                  boxSizing: "border-box",
                  color: "#324054",
                }}
                type="tel"
                name="whatsApp"
                placeholder="7x xx xx xxx"
                value={formData.whatsApp}
                onChange={handleChange}
                pattern="[0-9]*"
                inputMode="numeric"
              />
            </div>
            {errors.whatsApp && (
              <span
                style={{
                  color: "#EF4444",
                  fontSize: "12px",
                  marginTop: "4px",
                  display: "block",
                }}
              >
                {errors.whatsApp}
              </span>
            )}
          </div>

          <div>
            <label
              style={{
                display: "block",
                textAlign: "left",
                fontSize: "16px",
                fontWeight: 500,
                color: "#324054",
                marginBottom: "8px",
              }}
            >
              Role <span style={{ color: "#EF4444" }}>*</span>
            </label>
            <select
              style={{
                width: "100%",
                padding: "10px 12px",
                border: `1px solid ${errors.userRoleId ? "#EF4444" : "#E5E7EB"}`,
                borderRadius: "4px",
                fontSize: "16px",
                fontWeight: 500,
                outline: "none",
                fontFamily: "'Poppins', sans-serif",
                boxSizing: "border-box",
                color: "#324054",
              }}
              name="userRoleId"
              value={formData.userRoleId}
              onChange={handleChange}
            >
              <option value="">Select role</option>
              {userRoles.map((user) => (
                <option key={user.userRoleId} value={user.userRoleId}>
                  {user.roleName}
                </option>
              ))}
            </select>
            {errors.userRoleId && (
              <span
                style={{
                  color: "#EF4444",
                  fontSize: "12px",
                  marginTop: "4px",
                  display: "block",
                }}
              >
                {errors.userRoleId}
              </span>
            )}
          </div>

          <div>
            <label
              style={{
                display: "block",
                textAlign: "left",
                fontSize: "16px",
                fontWeight: 500,
                color: "#324054",
                marginBottom: "8px",
              }}
            >
              References <span style={{ color: "#EF4444" }}>*</span>
            </label>
            <MultiSelect
              options={projects}
              optionLabel={"name"}
              optionValue={"id"}
              selected={selectedRefereces}
              onChange={(references) => handleSelecteReferences(references)}
              error={errors.references}
            />
          </div>

          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: "12px",
              marginTop: "16px",
              paddingTop: "16px",
              position: "sticky",
              bottom: 0,
              backgroundColor: "#FFFFFF",
              paddingBottom: "8px",
            }}
          >
            <button
              type="button"
              style={{
                padding: "8px 16px",
                borderRadius: "4px",
                fontWeight: 500,
                fontSize: "14px",
                cursor: "pointer",
                minWidth: "120px",
                fontFamily: "'Poppins', sans-serif",
                backgroundColor: "#FFFFFF",
                color: "#324054",
                border: "1px solid #324054",
              }}
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              type="submit"
              style={{
                padding: "8px 16px",
                borderRadius: "4px",
                fontWeight: 500,
                fontSize: "14px",
                cursor: "pointer",
                minWidth: "120px",
                fontFamily: "'Poppins', sans-serif",
                backgroundColor: "#2563EB",
                color: "#FFFFFF",
                border: "none",
                opacity: loading ? 0.3 : 1,
              }}
              disabled={loading}
            >
              Create User
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddUserPopup;
